// src/components/admin/performance/ReviewConfirmationModal.js
import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Loader, Star, AlertCircle, History } from 'lucide-react';
import performanceService from '../../../services/performance.service';

const ReviewConfirmationModal = ({ isOpen, onClose, review }) => {
    const [formData, setFormData] = useState({
        overall_rating: '',
        technical_skills: '',
        communication_skills: '',
        teamwork: '',
        problem_solving: '',
        leadership: '',
        strengths: '',
        areas_for_improvement: '',
        goals: '',
        manager_comments: '',
        admin_comments: ''
    });

    const [originalData, setOriginalData] = useState({});
    const [changes, setChanges] = useState([]);
    const [changeReason, setChangeReason] = useState('');
    const [showActivityLog, setShowActivityLog] = useState(false);
    const [activityLog, setActivityLog] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingLog, setLoadingLog] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && review) {
            const data = {
                overall_rating: review.overall_rating || '',
                technical_skills: review.technical_skills || '',
                communication_skills: review.communication_skills || '',
                teamwork: review.teamwork || '',
                problem_solving: review.problem_solving || '',
                leadership: review.leadership || '',
                strengths: review.strengths || '',
                areas_for_improvement: review.areas_for_improvement || '',
                goals: review.goals || '',
                manager_comments: review.manager_comments || '',
                admin_comments: review.admin_comments || ''
            };
            setFormData(data);
            setOriginalData(data);
            setChanges([]);
            setChangeReason('');
            fetchActivityLog();
        }
    }, [isOpen, review]);

    // Auto-calculate overall rating when skill ratings change
    useEffect(() => {
        const skills = [
            parseFloat(formData.technical_skills) || 0,
            parseFloat(formData.communication_skills) || 0,
            parseFloat(formData.teamwork) || 0,
            parseFloat(formData.problem_solving) || 0,
            parseFloat(formData.leadership) || 0
        ].filter(v => v > 0);

        if (skills.length > 0) {
            const avg = skills.reduce((sum, val) => sum + val, 0) / skills.length;
            setFormData(prev => ({ ...prev, overall_rating: avg.toFixed(1) }));
        }
    }, [
        formData.technical_skills,
        formData.communication_skills,
        formData.teamwork,
        formData.problem_solving,
        formData.leadership
    ]);

    const fetchActivityLog = async () => {
        if (!review?.id) return;
        try {
            setLoadingLog(true);
            const log = await performanceService.getReviewActivityLog(review.id);
            setActivityLog(log);
        } catch (err) {
            console.error('Error fetching activity log:', err);
        } finally {
            setLoadingLog(false);
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const detectChanges = () => {
        const detectedChanges = [];
        const fieldLabels = {
            overall_rating: 'Overall Rating',
            technical_skills: 'Technical Skills',
            communication_skills: 'Communication Skills',
            teamwork: 'Teamwork',
            problem_solving: 'Problem Solving',
            leadership: 'Leadership',
            strengths: 'Strengths',
            areas_for_improvement: 'Areas for Improvement',
            goals: 'Goals',
            manager_comments: 'Manager Comments',
            admin_comments: 'Admin Comments'
        };

        Object.keys(formData).forEach(field => {
            const oldValue = String(originalData[field] || '');
            const newValue = String(formData[field] || '');

            if (oldValue !== newValue) {
                detectedChanges.push({
                    field_name: fieldLabels[field] || field,
                    old_value: oldValue,
                    new_value: newValue,
                    change_type: 'modified'
                });
            }
        });

        return detectedChanges;
    };

    const handleConfirm = async () => {
        try {
            setLoading(true);
            setError(null);

            const detectedChanges = detectChanges();

            // Require change reason if there are modifications
            if (detectedChanges.length > 0 && !changeReason.trim()) {
                setError('Please provide a reason for the changes made to this review');
                setLoading(false);
                return;
            }

            // Add change reason to all detected changes
            const changesWithReason = detectedChanges.map(change => ({
                ...change,
                change_reason: changeReason
            }));

            const confirmationData = {
                overall_rating: parseFloat(formData.overall_rating),
                technical_skills: parseFloat(formData.technical_skills),
                communication_skills: parseFloat(formData.communication_skills),
                teamwork: parseFloat(formData.teamwork),
                problem_solving: parseFloat(formData.problem_solving),
                leadership: parseFloat(formData.leadership),
                strengths: formData.strengths,
                areas_for_improvement: formData.areas_for_improvement,
                goals: formData.goals,
                manager_comments: formData.manager_comments,
                admin_comments: formData.admin_comments,
                changes: changesWithReason
            };

            await performanceService.adminConfirmReview(review.id, confirmationData);
            onClose();
        } catch (err) {
            console.error('Error confirming review:', err);
            setError(err.response?.data?.error || 'Failed to confirm review');
        } finally {
            setLoading(false);
        }
    };

    const renderStarRating = (name, value, label, originalValue) => {
        const hasChanged = String(value) !== String(originalValue);

        return (
            <div className={`p-3 rounded-lg ${hasChanged ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {label}
                    </label>
                    {hasChanged && (
                        <span className="text-xs text-yellow-600 dark:text-yellow-400 font-semibold">
                            Changed from {originalValue}/5
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((rating) => (
                        <button
                            key={rating}
                            type="button"
                            onClick={() => handleInputChange(name, rating.toString())}
                            className="p-2 rounded-lg transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <Star
                                className={`w-6 h-6 ${
                                    parseFloat(value) >= rating
                                        ? 'fill-yellow-400 text-yellow-400'
                                        : 'text-gray-300 dark:text-gray-600'
                                }`}
                            />
                        </button>
                    ))}
                    <span className="ml-2 text-sm font-semibold text-gray-600 dark:text-gray-400">
                        {value ? `${value} / 5.0` : 'Not rated'}
                    </span>
                </div>
            </div>
        );
    };

    if (!isOpen) return null;

    const detectedChanges = detectChanges();
    const hasChanges = detectedChanges.length > 0;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Review & Confirm Manager Evaluation
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {review?.first_name} {review?.last_name} - {review?.designation}
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setShowActivityLog(!showActivityLog)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                            <History className="w-4 h-4" />
                            Activity Log ({activityLog.length})
                        </button>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                            <X className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Main Form - 2 columns */}
                        <div className="lg:col-span-2 space-y-6">
                            {/* Error Message */}
                            {error && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-3">
                                    <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                    <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                                </div>
                            )}

                            {/* Review Info */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                        <span className="text-blue-700 dark:text-blue-300 font-medium">Reviewed By:</span>
                                        <p className="text-blue-900 dark:text-blue-200 font-semibold">
                                            {review?.reviewer_first_name} {review?.reviewer_last_name}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-blue-700 dark:text-blue-300 font-medium">Review Cycle:</span>
                                        <p className="text-blue-900 dark:text-blue-200 font-semibold capitalize">
                                            {review?.review_cycle}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-blue-700 dark:text-blue-300 font-medium">Period:</span>
                                        <p className="text-blue-900 dark:text-blue-200">
                                            {new Date(review?.review_period_start).toLocaleDateString()} - {new Date(review?.review_period_end).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <div>
                                        <span className="text-blue-700 dark:text-blue-300 font-medium">Submitted:</span>
                                        <p className="text-blue-900 dark:text-blue-200">
                                            {new Date(review?.submitted_to_admin_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Skill Ratings */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                    Performance Ratings
                                </h3>

                                {renderStarRating('technical_skills', formData.technical_skills, 'Technical Skills', originalData.technical_skills)}
                                {renderStarRating('communication_skills', formData.communication_skills, 'Communication Skills', originalData.communication_skills)}
                                {renderStarRating('teamwork', formData.teamwork, 'Teamwork', originalData.teamwork)}
                                {renderStarRating('problem_solving', formData.problem_solving, 'Problem Solving', originalData.problem_solving)}
                                {renderStarRating('leadership', formData.leadership, 'Leadership', originalData.leadership)}

                                {/* Overall Rating */}
                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-blue-900 dark:text-blue-300">
                                            Overall Rating (Average)
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                                            <span className="text-lg font-bold text-blue-900 dark:text-blue-300">
                                                {formData.overall_rating || '0.0'} / 5.0
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Text Fields */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                                    Manager's Feedback
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Strengths
                                    </label>
                                    <textarea
                                        value={formData.strengths}
                                        onChange={(e) => handleInputChange('strengths', e.target.value)}
                                        rows="3"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            formData.strengths !== originalData.strengths
                                                ? 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                                                : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Areas for Improvement
                                    </label>
                                    <textarea
                                        value={formData.areas_for_improvement}
                                        onChange={(e) => handleInputChange('areas_for_improvement', e.target.value)}
                                        rows="3"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            formData.areas_for_improvement !== originalData.areas_for_improvement
                                                ? 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                                                : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Goals for Next Period
                                    </label>
                                    <textarea
                                        value={formData.goals}
                                        onChange={(e) => handleInputChange('goals', e.target.value)}
                                        rows="3"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            formData.goals !== originalData.goals
                                                ? 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                                                : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Manager Comments
                                    </label>
                                    <textarea
                                        value={formData.manager_comments}
                                        onChange={(e) => handleInputChange('manager_comments', e.target.value)}
                                        rows="4"
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            formData.manager_comments !== originalData.manager_comments
                                                ? 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                                                : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Admin Comments
                                    </label>
                                    <textarea
                                        value={formData.admin_comments}
                                        onChange={(e) => handleInputChange('admin_comments', e.target.value)}
                                        rows="3"
                                        placeholder="Add any additional comments as admin..."
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${
                                            formData.admin_comments !== originalData.admin_comments
                                                ? 'border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20'
                                                : 'border-gray-300 dark:border-gray-600'
                                        }`}
                                    />
                                </div>

                                {/* Change Reason - Required if changes detected */}
                                {hasChanges && (
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                                        <label className="block text-sm font-semibold text-yellow-900 dark:text-yellow-300 mb-2">
                                            Reason for Changes *
                                        </label>
                                        <textarea
                                            value={changeReason}
                                            onChange={(e) => setChangeReason(e.target.value)}
                                            rows="2"
                                            placeholder="Explain why these changes were made..."
                                            required
                                            className="w-full px-3 py-2 border border-yellow-300 dark:border-yellow-600 rounded-lg focus:ring-2 focus:ring-yellow-500 dark:bg-gray-700 dark:text-white"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Activity Log Sidebar - 1 column */}
                        {showActivityLog && (
                            <div className="lg:col-span-1">
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 sticky top-0">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                        <History className="w-4 h-4" />
                                        Activity Log
                                    </h3>

                                    {loadingLog ? (
                                        <div className="flex justify-center py-8">
                                            <Loader className="w-6 h-6 animate-spin text-blue-600" />
                                        </div>
                                    ) : activityLog.length === 0 ? (
                                        <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-8">
                                            No changes recorded yet
                                        </p>
                                    ) : (
                                        <div className="space-y-3 max-h-[600px] overflow-y-auto">
                                            {activityLog.map((activity) => {
                                                // Parse the changes JSONB field
                                                const changes = typeof activity.changes === 'string'
                                                    ? JSON.parse(activity.changes)
                                                    : activity.changes;

                                                return (
                                                    <div key={activity.id} className="bg-white dark:bg-gray-800 rounded-lg p-3 text-xs">
                                                        <div className="flex items-start justify-between mb-2">
                                                            <span className="font-semibold text-gray-900 dark:text-white">
                                                                {activity.first_name} {activity.last_name}
                                                            </span>
                                                            <span className="text-gray-500 dark:text-gray-400">
                                                                {new Date(activity.created_at).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                        <div className="text-gray-700 dark:text-gray-300 mb-2">
                                                            <span className="font-medium capitalize">{activity.action.replace(/_/g, ' ')}</span>
                                                        </div>
                                                        {Array.isArray(changes) && changes.length > 0 && (
                                                            <div className="space-y-2">
                                                                {changes.map((change, idx) => (
                                                                    <div key={idx} className="border-l-2 border-blue-300 dark:border-blue-700 pl-2">
                                                                        <div className="font-medium text-gray-800 dark:text-gray-200">
                                                                            {change.field_name}
                                                                        </div>
                                                                        {change.old_value && (
                                                                            <div className="text-gray-500 dark:text-gray-400 line-through text-[10px]">
                                                                                Old: {change.old_value}
                                                                            </div>
                                                                        )}
                                                                        {change.new_value && (
                                                                            <div className="text-green-600 dark:text-green-400 text-[10px]">
                                                                                New: {change.new_value}
                                                                            </div>
                                                                        )}
                                                                        {change.change_reason && (
                                                                            <div className="mt-1 text-gray-600 dark:text-gray-400 italic text-[10px]">
                                                                                "{change.change_reason}"
                                                                            </div>
                                                                        )}
                                                                        {change.admin_comments && (
                                                                            <div className="mt-1 text-blue-600 dark:text-blue-400 text-[10px]">
                                                                                {change.admin_comments}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>

                    <button
                        onClick={handleConfirm}
                        disabled={loading || (hasChanges && !changeReason.trim())}
                        className="inline-flex items-center px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <Loader className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                            <CheckCircle className="w-4 h-4 mr-2" />
                        )}
                        Confirm & Finalize Review
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReviewConfirmationModal;
